import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import { ACTORS_QUERY_KEYS } from "Actors/Api/Queries/queryKeys";
import {
  ModuleType,
  type ProjectDetailDto,
  ProjectState,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { BackButton } from "Shared/Buttons/BackButton";
import { BlConfirmDialog } from "Shared/Dialogs/BlConfirmDialog";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";
import { StatusChip } from "Shared/StatusChip";
import { useCreateJobMutation } from "../Api/Mutations/useCreateJobMutation";
import { useSyncProjectActorsMutation } from "../Api/Mutations/useSyncProjectActorsMutation";
import { useUpdateProjectStateMutation } from "../Api/Mutations/useUpdateProjectStateMutation";
import { useProjectDetailQuery } from "../Api/Queries/useProjectDetailQuery";
import { useProjectJobsQuery } from "../Api/Queries/useProjectJobsQuery";
import {
  type CreateJobFormData,
  CreateJobModal,
} from "../Components/CreateJobModal";
import { EditProjectModal } from "../Components/EditProjectModal";
import { ProjectActors } from "../Components/ProjectActors";
import { ProjectActorsModal } from "../Components/ProjectActorsModal";
import { ProjectDocumentsSection } from "../Components/ProjectDocumentsSection";
import { ProjectInformation } from "../Components/ProjectInformation";
import { ProjectJobs } from "../Components/ProjectJobs";

export const ProjectDetailPage = (): React.ReactNode => {
  const { t } = useResource();

  const { projectId } = useParams<{ projectId: string }>();
  const { data: project, isLoading } = useProjectDetailQuery(projectId!);
  const { data: jobsData } = useProjectJobsQuery(projectId!);
  const jobs = jobsData?.items ?? [];
  const [isCreateJobModalOpen, setIsCreateJobModalOpen] = useState(false);
  const [isActorsModalOpen, setIsActorsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [isFinishConfirmOpen, setIsFinishConfirmOpen] = useState(false);
  const createJobMutation = useCreateJobMutation(projectId!);
  const syncActorsMutation = useSyncProjectActorsMutation(projectId!);
  const updateStateMutation = useUpdateProjectStateMutation(projectId!);
  const queryClient = useQueryClient();

  const isEditable =
    project?.projectState === ProjectState.InProgress ||
    project?.projectState === ProjectState.Draft;

  const isSingleVoiceSpot = project?.moduleType === ModuleType.SingleVoiceSpot;

  const handleCreateJob = async (jobData: CreateJobFormData) => {
    try {
      await createJobMutation.mutateAsync(jobData.name);
      toast.success(t(Resources.Projects.CreateJobModal.SuccessMessage));
      setIsCreateJobModalOpen(false);
    } catch {
      // auto-toast shown by mutation hook
    }
  };

  const handleSyncActors = async (actorIds: string[]) => {
    try {
      await syncActorsMutation.mutateAsync(actorIds);
      queryClient.invalidateQueries({
        queryKey: ACTORS_QUERY_KEYS.all,
      });

      toast.success(t(Resources.Projects.ProjectActorsModal.SuccessMessage));
      setIsActorsModalOpen(false);
    } catch {
      // auto-toast shown by mutation hook
    }
  };

  const handleCloseProject = async (confirmed: boolean) => {
    setIsCloseConfirmOpen(false);
    if (!confirmed) return;
    try {
      await updateStateMutation.mutateAsync(ProjectState.Closed);
      toast.success(t(Resources.Projects.ProjectDetail.CloseProject));
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  const handleFinishProject = async (confirmed: boolean) => {
    setIsFinishConfirmOpen(false);
    if (!confirmed) return;
    try {
      await updateStateMutation.mutateAsync(ProjectState.Done);
      toast.success(t(Resources.Projects.ProjectDetail.FinishProject));
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Projects)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  if (!project) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Projects)}>
        <Typography>
          {t(Resources.Projects.ProjectDetail.ProjectNotFound)}
        </Typography>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout
      preTitle={
        <BackButton
          defaultText={t(Resources.Common.BackToProjects)}
          defaultPath={AppRouting.Projects.path()}
        />
      }
      title={project.name}
      subtitle={project.moduleName}
      titleEndAdornment={<StatusChip projectState={project.projectState} />}
      action={
        isEditable ? (
          <Stack direction="row" spacing={Spacing.S}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsCloseConfirmOpen(true)}
              disabled={updateStateMutation.isPending}
              sx={{ textTransform: "none" }}
            >
              {t(Resources.Projects.ProjectDetail.CloseProject)}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => setIsFinishConfirmOpen(true)}
              disabled={updateStateMutation.isPending}
              sx={{ textTransform: "none" }}
            >
              {t(Resources.Projects.ProjectDetail.FinishProject)}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <Grid container spacing={Spacing.M}>
        <Grid size={6}>
          <Stack spacing={Spacing.M}>
            <ProjectInformation
              industry={project.industry}
              clientName={project.clientName ?? null}
              clientID={project.clientID ?? null}
              language={project.language ?? null}
              licensedCountries={project.licensedCountries}
              jobsLimit={project.jobsLimit}
              currentJobs={jobs.length}
              startDate={project.startDate ?? null}
              endDate={project.endDate ?? null}
              onEdit={() => setIsEditModalOpen(true)}
              isEditable={isEditable}
            />
            <ProjectDocumentsSection
              projectID={projectId!}
              isEditable={isEditable}
            />
          </Stack>
        </Grid>
        <Grid size={6}>
          <Stack spacing={Spacing.M}>
            <ProjectJobs
              jobs={jobs}
              projectId={projectId!}
              onAddJob={() => setIsCreateJobModalOpen(true)}
              isEditable={isEditable}
              jobsLimit={project.jobsLimit}
              currentJobsCount={jobs.length}
            />
            <ProjectActors
              actors={project.actors}
              onAddActor={() => setIsActorsModalOpen(true)}
              isEditable={isEditable}
              isSingleVoiceSpot={isSingleVoiceSpot}
              hasBlockingJobs={project.hasBlockingJobsForActorChange}
            />
          </Stack>
        </Grid>
      </Grid>

      <EditProjectModal
        isOpen={isEditModalOpen}
        project={project as ProjectDetailDto}
        onClose={() => setIsEditModalOpen(false)}
      />

      <CreateJobModal
        isOpen={isCreateJobModalOpen}
        onClose={() => setIsCreateJobModalOpen(false)}
        onConfirm={handleCreateJob}
      />

      <ProjectActorsModal
        isOpen={isActorsModalOpen}
        onClose={() => setIsActorsModalOpen(false)}
        onConfirm={handleSyncActors}
        currentActors={project.actors}
        currentActorIds={project.actors.map((a) => a.actorID)}
        exclusiveActorIds={project.actors
          .filter((a) => a.isExclusiveContract)
          .map((a) => a.actorID)}
        blockedActorIds={project.actors
          .filter((a) => a.hasBlockingJobs)
          .map((a) => a.actorID)}
        maxActors={isSingleVoiceSpot ? 1 : undefined}
        priceCategoryID={project.priceCategoryID}
        voiceLlms={project.moduleVoiceLlms}
        industry={project.industry}
      />

      <BlConfirmDialog
        isOpen={isCloseConfirmOpen}
        handleResult={handleCloseProject}
        modalTitle={t(
          Resources.Projects.ProjectDetail.CloseProjectConfirmTitle,
        )}
        modalContent={t(
          Resources.Projects.ProjectDetail.CloseProjectConfirmContent,
        )}
        confirmButtonText={t(Resources.Projects.ProjectDetail.CloseProject)}
      />

      <BlConfirmDialog
        isOpen={isFinishConfirmOpen}
        handleResult={handleFinishProject}
        modalTitle={t(
          Resources.Projects.ProjectDetail.FinishProjectConfirmTitle,
        )}
        modalContent={t(
          Resources.Projects.ProjectDetail.FinishProjectConfirmContent,
        )}
        confirmButtonText={t(Resources.Projects.ProjectDetail.FinishProject)}
      />
    </AuthenticatedLayout>
  );
};
